import { createSlice } from "@reduxjs/toolkit";
import {
  AmountPayload,
  AssetTimerPayload,
  GraphPayload,
  HomeSlice,
  RemoveSelectedAssetPayload,
  SelectAssetPayload,
  TimerPayload,
} from "../../types/slices/HomeSlice";
import { AssetSymbol, Timer } from "../../types/models/AssetSymbol";

const initialActiveSymbol = (): string => {
  const activeSymbol = window.localStorage.getItem("activeSymbol");
  //** Parse stored json or if none return initialValue
  return activeSymbol ? JSON.parse(activeSymbol) : "META";
};

const initialAselectedSymbols = (): AssetSymbol[] => {
  const selectedSymbols = window.localStorage.getItem("selectedSymbols");
  //** Parse stored json or if none return initialValue
  const defaultSymbols: AssetSymbol[] = [
    {
      symbol: "META",
      name: "Meta Platforms, Inc.",
      price: 571.795,
      exchange: "NASDAQ Global Select",
      exchangeShortName: "NASDAQ",
      type: "stock",
      logo: "https://images.financialmodelingprep.com/symbol/META.png",
    },
  ];
  return selectedSymbols ? JSON.parse(selectedSymbols) : defaultSymbols;
};

const initialState: HomeSlice = {
  amountDialog: false,
  timerDialog: false,
  assetDialog: false,
  graphDialog: false,
  activeSymbol: initialActiveSymbol(),
  selectedSymbols: initialAselectedSymbols(),
  assetTimers: [],
  currentTimer: undefined,
  amount: 1,
  interval: "1",
  graphStyle: "1",
};

export const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {
    toggleAmountDialog: (state) => {
      state.amountDialog = !state.amountDialog;
    },
    toggleTimerDialog: (state) => {
      state.timerDialog = !state.timerDialog;
    },
    toggleAssetDialog: (state) => {
      state.assetDialog = !state.assetDialog;
    },
    toggleGraphDialog: (state) => {
      state.graphDialog = !state.graphDialog;
    },
    onSelectAsset: (state, { payload }: SelectAssetPayload) => {
      const { asset, rearange } = payload;
      const selectedSymbols = state.selectedSymbols;
      const alreadySelected: AssetSymbol | undefined = selectedSymbols.find(
        (item: AssetSymbol) => item.symbol === asset.symbol
      );
      if (!alreadySelected) {
        if (rearange) {
          if (selectedSymbols.length === 4) {
            selectedSymbols.shift();
            selectedSymbols.push(asset);
          } else {
            selectedSymbols.push(asset);
          }
        }
      }

      state.activeSymbol = asset.symbol;
      state.selectedSymbols = selectedSymbols;
      localStorage.setItem("activeSymbol", JSON.stringify(asset.symbol));
      localStorage.setItem("selectedSymbols", JSON.stringify(selectedSymbols));
    },
    onRemoveSelectedAsset: (state, { payload }: RemoveSelectedAssetPayload) => {
      const { asset } = payload;
      const selectedSymbols = state.selectedSymbols.filter(
        (item: AssetSymbol) => item.symbol !== asset.symbol
      );
      const lastActive: AssetSymbol = selectedSymbols.slice(-1)[0];
      state.activeSymbol = lastActive.symbol;
      state.selectedSymbols = selectedSymbols;
      localStorage.setItem("activeSymbol", JSON.stringify(asset.symbol));
      localStorage.setItem("selectedSymbols", JSON.stringify(selectedSymbols));
    },
    setAssetTimers: (state, { payload }: AssetTimerPayload) => {
      const { assetTimers } = payload;
      state.assetTimers = assetTimers;
    },
    increaseAmount: (state) => {
      const isAmountNaN = isNaN(Number(state.amount));
      state.amount = isAmountNaN ? 1 : state.amount + 1;
    },
    decreaseAmount: (state) => {
      if (state.amount > 1) {
        state.amount = state.amount - 1;
      }
    },
    selectAmount: (state, { payload }: AmountPayload) => {
      const { amount } = payload;
      state.amount = amount;
    },
    setCurrentAssetTimer: (state, { payload }: TimerPayload) => {
      const { timer } = payload;
      state.currentTimer = timer;
    },
    increaseTimer: (state) => {
      if (state.currentTimer) {
        const currentTimer: Timer = state.currentTimer;
        const timers: Timer[] = state.assetTimers;
        const currentIndex = timers.findIndex(
          (t: Timer) => t.id === currentTimer.id
        );
        if (timers.length - 1 > currentIndex) {
          state.currentTimer = timers[currentIndex + 1];
        }
      }
    },
    decreaseTimer: (state) => {
      if (state.currentTimer) {
        const currentTimer: Timer = state.currentTimer;
        const timers: Timer[] = state.assetTimers;
        const currentIndex = timers.findIndex(
          (t: Timer) => t.id === currentTimer.id
        );
        if (timers.length > 1 && currentIndex > 0) {
          state.currentTimer = timers[currentIndex - 1];
        }
      }
    },
    setInterval: (state, { payload }) => {
      state.interval = payload;
    },
    setGraphStyle: (state, { payload }: GraphPayload) => {
      const { graphStyle } = payload;
      state.graphStyle = graphStyle;
    },
    setActiveSymbol: (state, { payload }) => {
      localStorage.setItem("activeSymbol", JSON.stringify(payload));
      state.activeSymbol = payload;
    },
  },
});

export const homeActions = homeSlice.actions;
